/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, memo, useCallback } from 'react';
import { getFeatureFlags } from 'app/appState';
import { Box } from 'entities/Box';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Collapse } from 'shared/ui/Collapse';
import { PromotionContent } from './PromotionContent';
import { SelectBoxSizeContent } from './SelectBoxSizeContent';
import { SelectOfficeContent } from './SelectOfficeContent';
import { NearestWarehousesList } from './NearestWarehousesList';
import { WarehouseForBooking, useGetWarehousesForBookingQuery } from 'entities/Warehouse';
import { UserAccountType } from 'entities/User';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { UnitTypeFilter, getSelectedUnitTypeFilter } from 'features/UnitTypeFilter';
import { UnitType } from 'entities/Contract';
import { Button } from 'shared/ui/Button';
import { ReactComponent as MapIcon } from 'shared/assets/icons/MapBlackIcon.svg';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { hasBookingFloorMap } from '../utils/helpers/getBookingFloorMapData';

interface SelectBoxSizeFormProps {
  selectedBoxId: string | undefined;
  selectedWarehouseId: string | undefined;
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onCompleteStep: (box: Box, sizeCodeId?: string) => void;
  onSelectWarehouse: (warehouse: WarehouseForBooking) => void;
  unitViewMode: 'list' | 'map';
  onSetUnitViewMap: () => void;
  onSetUnitViewList: () => void;
  warehouseInfo?: Nullable<WarehouseForBooking>;
  accountType?: Nullable<UserAccountType>;
  isShowPromotionPriceNeeded?: boolean;
}

export const SelectBoxSizeForm: FC<SelectBoxSizeFormProps> = memo((props) => {
  const {
    onSelectWarehouse,
    unitViewMode,
    onSetUnitViewMap,
    onSetUnitViewList,
    selectedBoxId,
    selectedWarehouseId,
    onSelectBox,
    onCompleteStep,
    warehouseInfo,
    accountType,
    isShowPromotionPriceNeeded,
  } = props;

  const { t } = useAppTranslation('booking');

  const unitSelectionContentProps = {
    selectedBoxId,
    selectedWarehouseId,
    onSelectBox,
    onCompleteStep,
  };

  const boxUnitSelectionContentProps = {
    ...unitSelectionContentProps,
    warehouseInfo,
    accountType,
    isShowPromotionPriceNeeded,
  };

  const { scrollToTop } = useScrollToElement();
  const { data } = useGetWarehousesForBookingQuery();
  const { data: floors } = useGetFloorsByWarehouseIdQuery(selectedWarehouseId, { skip: !selectedWarehouseId });
  const featureFlags = useAppSelector(getFeatureFlags);
  const isBookingMapEnabled = Boolean(featureFlags?.bookingMap);
  const isWarehouseMapAvailable = Boolean(floors?.some((floor) => hasBookingFloorMap(floor.shapesConfig)));
  const isBookingMapAvailable = isBookingMapEnabled && isWarehouseMapAvailable;

  const selectedUnitTypeFilter = useAppSelector(getSelectedUnitTypeFilter);

  const onSelect = useCallback(
    (warehouse: WarehouseForBooking) => {
      onSelectWarehouse(warehouse);
      scrollToTop();
    },
    [onSelectWarehouse, scrollToTop],
  );

  const renderUnits = (unitType: UnitType): React.ReactNode => {
    const unitTypeContentMap: Record<UnitType.BOX | UnitType.OFFICE, React.ReactNode> = {
      [UnitType.BOX]: (
        <Collapse
          title={t('Select the size of the box')}
          headerAction={
            isBookingMapAvailable ? (
              <Button
                theme="clear"
                size="small"
                fontSize="large"
                icon={<MapIcon className="[&>path]:stroke-accent" />}
                iconPosition="prev"
                onClick={unitViewMode === 'list' ? onSetUnitViewMap : onSetUnitViewList}
              >
                {t(unitViewMode === 'list' ? 'Open Map of the Warehouse' : 'Back to list')}
              </Button>
            ) : undefined
          }
          content={
            <SelectBoxSizeContent
              {...boxUnitSelectionContentProps}
              viewMode={unitViewMode}
              onSwitchToList={onSetUnitViewList}
              isMapAvailable={isBookingMapAvailable}
            />
          }
          activeKey="1"
          isOpened
        />
      ),
      [UnitType.OFFICE]: (
        <Collapse title={t('Select office')} content={<SelectOfficeContent {...unitSelectionContentProps} />} activeKey="2" isOpened />
      ),
    };

    if (unitType === UnitType.BOX || unitType === UnitType.OFFICE) {
      return unitTypeContentMap[unitType];
    }

    return <React.Fragment />;
  };

  return (
    <div className="space-y-3">
      <Collapse
        title={t('Promotion')}
        content={<PromotionContent selectedWarehouseId={props.selectedWarehouseId} />}
        activeKey="1"
        isOpened
      />
      <UnitTypeFilter />
      {renderUnits(selectedUnitTypeFilter)}
      {data && data.length > 1 && (
        <Collapse
          title={t('Warehouses near you')}
          content={<NearestWarehousesList onSelectWarehouse={onSelect} selectedWarehouseId={props.selectedWarehouseId} />}
          activeKey="3"
          isOpened
        />
      )}
    </div>
  );
});
